import AsyncStorage from "@react-native-async-storage/async-storage";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import { Appearance, StyleSheet, useColorScheme } from "react-native";

import {
  darkColors,
  lightColors,
  tokens,
  type ColorScheme,
  type ThemeColors,
} from "./tokens";

export type ThemePreference = ColorScheme | "system";

type ThemeContextValue = {
  preference: ThemePreference;
  scheme: ColorScheme;
  colors: ThemeColors;
  setPreference: (preference: ThemePreference) => void;
  toggleScheme: () => void;
  tokens: typeof tokens;
};

const STORAGE_KEY = "doughbro.theme";

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: PropsWithChildren) {
  const systemScheme = useColorScheme();
  const [preference, setPreferenceState] = useState<ThemePreference>("system");

  useEffect(() => {
    let active = true;
    void AsyncStorage.getItem(STORAGE_KEY).then((stored) => {
      if (!active) return;
      if (stored === "light" || stored === "dark" || stored === "system") {
        setPreferenceState(stored as ThemePreference);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  const scheme: ColorScheme =
    preference === "system"
      ? systemScheme === "dark"
        ? "dark"
        : "light"
      : preference;

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    void AsyncStorage.setItem(STORAGE_KEY, next);
    if (
      (next === "light" || next === "dark") &&
      typeof Appearance.setColorScheme === "function"
    ) {
      Appearance.setColorScheme(next);
    }
  }, []);

  const toggleScheme = useCallback(() => {
    setPreference(scheme === "dark" ? "light" : "dark");
  }, [scheme, setPreference]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      preference,
      scheme,
      colors: scheme === "dark" ? darkColors : lightColors,
      setPreference,
      toggleScheme,
      tokens,
    }),
    [preference, scheme, setPreference, toggleScheme],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
}

/** Safe fallback for modules that may render outside the provider in tests. */
export function useThemeColors(): ThemeColors {
  const context = useContext(ThemeContext);
  const system = useColorScheme();
  if (context) return context.colors;
  return system === "dark" ? darkColors : lightColors;
}

/**
 * Build a themed StyleSheet once per palette change. Use for screens whose
 * styles reference colours:
 *
 *   const styles = useThemedStyles((c) =>
 *     StyleSheet.create({ card: { backgroundColor: c.surface } }));
 */
export function useThemedStyles<T extends StyleSheet.NamedStyles<T>>(
  factory: (colors: ThemeColors) => T,
): T {
  const { colors } = useTheme();
  return useMemo(() => factory(colors), [colors, factory]);
}
